// Considered: DataListMolecule (wraps DataGridAtom with isSphereId batch-fetch) — rejected:
// scorecard rows already carry full enriched sphere data from executeContext, so the batch
// sphere-identity fetch that molecule exists for is unnecessary overhead. See spec Reuse Audit.
import React, { useMemo } from 'react';
import { useTranslation } from 'localization';
import type { SphereData } from '../../../hooks/useContextData';
import type { ScorecardViewConfig } from 'types/contextView';
import type { StatusValue } from 'types/spherePoints';
import type { GravityBucketKey } from 'types/sphereMetrics';
import DataGridAtom, { type DataGridColumn } from '../../atoms/DataGrid/DataGridAtom';
import StatusBadgeAtom, { type StatusBadgeTone } from '../../atoms/StatusBadge/StatusBadgeAtom';
import VelocityIndicator from '../../atoms/VelocityIndicator/VelocityIndicator';
import './ScorecardTableMolecule.scss';

const STATUS_LABEL_KEY: Record<StatusValue, string> = {
  on_track: 'sphereRow.status.onTrack',
  at_risk: 'sphereRow.status.atRisk',
  needs_attention: 'sphereRow.status.needsAttention',
  critical: 'sphereRow.status.critical',
  blocked: 'sphereRow.status.blocked',
  unknown: 'sphereRow.status.unknown',
};

const STATUS_TONE: Record<StatusValue, StatusBadgeTone> = {
  on_track: 'success',
  at_risk: 'warn',
  needs_attention: 'warn',
  critical: 'danger',
  blocked: 'danger',
  unknown: 'neutral',
};

const GRAVITY_TONE: Record<GravityBucketKey, StatusBadgeTone> = {
  critical: 'danger',
  high: 'warn',
  medium: 'info',
  low: 'neutral',
  minimal: 'neutral',
};

/**
 * `SphereData` carries an index signature, so `name` is `unknown` — only fall back to it
 * when it is actually a string, otherwise an object would render as "[object Object]".
 */
const sphereDisplayName = (row: SphereData): string =>
  row.label ?? (typeof row.name === 'string' ? row.name : row.id);

const StatusCell: React.FC<{ row: SphereData }> = ({ row }) => {
  const { t } = useTranslation();
  const status = row.status?.currentValue?.status ?? 'unknown';
  return (
    <StatusBadgeAtom
      tone={STATUS_TONE[status] ?? 'neutral'}
      label={t(STATUS_LABEL_KEY[status] ?? 'sphereRow.status.unknown')}
      data-testid={`scorecard-status-${row.id}`}
    />
  );
};

const VelocityCell: React.FC<{ row: SphereData }> = ({ row }) => {
  const { t } = useTranslation();
  const velocity = row.points?.velocity;
  if (!velocity) {
    return <span className="scorecard-table__empty-cell">{t('scorecard.table.noData')}</span>;
  }
  return <VelocityIndicator score={velocity.score} bucket={velocity.bucket} showScore />;
};

const GravityCell: React.FC<{ row: SphereData }> = ({ row }) => {
  const { t } = useTranslation();
  const gravity = row.points?.gravity;
  if (!gravity) {
    return <span className="scorecard-table__empty-cell">{t('scorecard.table.noData')}</span>;
  }
  return (
    <StatusBadgeAtom
      tone={GRAVITY_TONE[gravity.bucket] ?? 'neutral'}
      label={String(Math.round(gravity.score))}
      tooltip={t(`scorecard.table.gravityBuckets.${gravity.bucket}`)}
      data-testid={`scorecard-gravity-${row.id}`}
    />
  );
};

const nameBody = (row: SphereData) => sphereDisplayName(row);
const statusBody = (row: SphereData) => <StatusCell row={row} />;
const velocityBody = (row: SphereData) => <VelocityCell row={row} />;
const gravityBody = (row: SphereData) => <GravityCell row={row} />;

export interface ScorecardTableMoleculeProps {
  spheres: SphereData[];
  surfaceColumns: ScorecardViewConfig['surfaceColumns'];
  defaultSort?: ScorecardViewConfig['defaultSort'];
  loading?: boolean;
  error?: string | null;
  className?: string;
}

const ScorecardTableMolecule: React.FC<ScorecardTableMoleculeProps> = ({
  spheres,
  surfaceColumns,
  defaultSort,
  loading = false,
  error = null,
  className = '',
}) => {
  const { t } = useTranslation();

  const columns = useMemo<DataGridColumn[]>(() => {
    const cols: DataGridColumn[] = [
      {
        field: 'label',
        header: t('scorecard.table.columns.name'),
        sortable: true,
        body: nameBody,
      },
    ];

    if (surfaceColumns.showStatus) {
      cols.push({
        field: 'status',
        header: t('scorecard.table.columns.status'),
        sortable: true,
        body: statusBody,
      });
    }

    if (surfaceColumns.showVelocity) {
      cols.push({
        field: 'velocity',
        header: t('scorecard.table.columns.velocity'),
        sortable: true,
        body: velocityBody,
      });
    }

    if (surfaceColumns.showGravity) {
      cols.push({
        field: 'gravity',
        header: t('scorecard.table.columns.gravity'),
        sortable: true,
        body: gravityBody,
      });
    }

    return cols;
  }, [surfaceColumns, t]);

  return (
    <div className={`scorecard-table ${className}`.trim()} data-testid="scorecard-table">
      <DataGridAtom
        data={spheres}
        columns={columns}
        loading={loading}
        error={error}
        emptyMessage={t('scorecard.table.empty')}
        sortField={defaultSort?.column}
        sortOrder={defaultSort?.direction === 'desc' ? -1 : 1}
      />
    </div>
  );
};

export default ScorecardTableMolecule;
